/**
 * Distribution Factory - Dependency Injection
 *
 * This factory class provides dependency injection for the distribution feature.
 * It creates and manages instances of repositories and use cases.
 *
 * @module DistributionFactory
 */

import {DistributionRepositoryImpl} from '../repository/DistributionRepositoryImpl';
import {GetDistributionHistoryUseCase} from '../useCases/GetDistributionHistoryUseCase';
import {CreateDeliveryUseCase} from '../useCases/CreateDeliveryUseCase';
import {GetTrackedSerialsUseCase} from '../useCases/GetTrackedSerialsUseCase';
import {DeleteTransactionUseCase} from '../useCases/DeleteTransactionUseCase';
import {GetMarketersForDistributionUseCase} from '../useCases/GetMarketersForDistributionUseCase';
import {GetProductsForDistributionUseCase} from '../useCases/GetProductsForDistributionUseCase';
import {GetAvailableSerialsUseCase} from '../useCases/GetAvailableSerialsUseCase';
import {GetPendingDeliveriesUseCase} from '../useCases/GetPendingDeliveriesUseCase';
import {GetCompletedDeliveriesUseCase} from '../useCases/GetCompletedDeliveriesUseCase';
import {FilterDistributionRecordsUseCase} from '../useCases/FilterDistributionRecordsUseCase';

// Factory class for Distribution feature
export class DistributionFactory {
  private static distributionRepository: DistributionRepositoryImpl | null = null;

  // Repository Singleton
  private static getDistributionRepository (): DistributionRepositoryImpl {
    if (!this.distributionRepository) {
      this.distributionRepository = new DistributionRepositoryImpl();
    }
    return this.distributionRepository;
  }

  // Use Cases Factory Methods
  static createGetDistributionHistoryUseCase (): GetDistributionHistoryUseCase {
    const repository = this.getDistributionRepository();
    return new GetDistributionHistoryUseCase(repository);
  }

  static createCreateDeliveryUseCase (): CreateDeliveryUseCase {
    const repository = this.getDistributionRepository();
    return new CreateDeliveryUseCase(repository);
  }

  static createGetTrackedSerialsUseCase (): GetTrackedSerialsUseCase {
    const repository = this.getDistributionRepository();
    return new GetTrackedSerialsUseCase(repository);
  }

  static createDeleteTransactionUseCase (): DeleteTransactionUseCase {
    const repository = this.getDistributionRepository();
    return new DeleteTransactionUseCase(repository);
  }

  static createGetMarketersForDistributionUseCase (): GetMarketersForDistributionUseCase {
    const repository = this.getDistributionRepository();
    return new GetMarketersForDistributionUseCase(repository);
  }

  static createGetProductsForDistributionUseCase (): GetProductsForDistributionUseCase {
    const repository = this.getDistributionRepository();
    return new GetProductsForDistributionUseCase(repository);
  }

  static createGetAvailableSerialsUseCase (): GetAvailableSerialsUseCase {
    const repository = this.getDistributionRepository();
    return new GetAvailableSerialsUseCase(repository);
  }

  // Business Logic Use Cases (no repository dependency)
  static createGetPendingDeliveriesUseCase (): GetPendingDeliveriesUseCase {
    return new GetPendingDeliveriesUseCase();
  }

  static createGetCompletedDeliveriesUseCase (): GetCompletedDeliveriesUseCase {
    return new GetCompletedDeliveriesUseCase();
  }

  static createFilterDistributionRecordsUseCase (): FilterDistributionRecordsUseCase {
    return new FilterDistributionRecordsUseCase();
  }

  // Reset factory (useful for testing)
  static reset (): void {
    this.distributionRepository = null;
  }
}
